/**
 * Sentry initialisation, DSGVO-aware.
 *
 * Three things matter here:
 *
 *  1. **Boot order.** Init runs in `main.tsx` *before* React mounts so
 *     the very first render's errors are captured.
 *
 *  2. **No PHI / PII.** `beforeSend` strips request bodies, cookies,
 *     and URL fragments that contain patient ids. The user context we
 *     attach (see `setSentryUser`) is the *organisation id* only — never
 *     the user email or name.
 *
 *  3. **Release tagging.** Every event is tagged with
 *     `nutriflow-pro@<package version>`. That value matches the release
 *     name uploaded by `@sentry/vite-plugin` in CI, so Sentry can
 *     resolve minified stack traces back to source.
 */

import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { env } from './env';

// Regex wird modul-global definiert, damit beide Redaction-Stellen denselben
// Ausdruck teilen und die Unit-Tests einen expliziten Fixpunkt haben.
const PATIENT_ID_IN_URL_RE = /\/patients\/[a-f0-9-]+/g;
const PATIENT_REDACTED_PATH = '/patients/[REDACTED]';

// Pattern für PHI in freien Strings (Messages, Stacktraces). Konservativ
// gehalten — false positives lieber als false negatives, weil ein
// einziger durchgelassener Patientenname ein DSGVO-Vorfall sein kann.
//
// Email:    user@host.tld
// KVNR:     ein Buchstabe + 9 Ziffern (z. B. A123456789)
// UUID:     Patient-/Encounter-IDs in der Form 8-4-4-4-12
const EMAIL_RE = /\b[\w.+-]+@[\w-]+\.[\w.-]{2,}\b/g;
const KVNR_RE  = /\b[A-Za-z]\d{9}\b/g;
const UUID_RE  = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

// Keys, deren Werte in jedem Sentry-Kontext (extra, contexts, tags) als PHI
// behandelt werden. Symmetrisch zur Liste in
// supabase/functions/_shared/logger.ts.
const PHI_KEY_DENYLIST = new Set([
  'email',
  'first_name', 'last_name', 'full_name', 'name', 'patient_name',
  'phone', 'phone_number',
  'date_of_birth', 'dob', 'birth_date',
  'address', 'street', 'postal_code', 'city',
  'insurance_number', 'kvnr', 'kvid',
  'external_patient_id', 'mrn', 'patient_id', 'case_number',
  'room_bed', 'room', 'bed', 'station',
  'ssn', 'social_security',
]);

const REDACTED = '[REDACTED]';

/**
 * Redact PHI in a free-text string. Order matters — emails before
 * KVNR before UUIDs, because an email can contain a digit-string that
 * would otherwise look like a KVNR.
 *
 * Pure: returns a new string, never throws.
 */
export function redactPhiInString(value: string): string {
  if (!value) return value;
  return value
    .replace(EMAIL_RE, REDACTED)
    .replace(KVNR_RE, REDACTED)
    .replace(UUID_RE, REDACTED);
}

/**
 * Recursively walk a value tree and redact:
 *   - strings via `redactPhiInString`
 *   - any object key in `PHI_KEY_DENYLIST` → its value becomes `[REDACTED]`
 *   - arrays/objects: recursive
 *
 * Returns a NEW value tree; the original is not mutated.
 */
export function redactPhiInValue(value: unknown): unknown {
  if (value == null) return value;
  if (typeof value === 'string') return redactPhiInString(value);
  if (typeof value !== 'object') return value;

  if (Array.isArray(value)) return value.map(redactPhiInValue);

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (PHI_KEY_DENYLIST.has(k.toLowerCase())) {
      out[k] = REDACTED;
    } else {
      out[k] = redactPhiInValue(v);
    }
  }
  return out;
}

/**
 * Strippt personenbezogene Daten aus einem Sentry-Event, bevor es an den
 * Sentry-Server gesendet wird. Exportiert (nicht nur inline in beforeSend),
 * damit die Logik per Unit-Test gegen die im Audit 22.04.2026 beschriebenen
 * Anforderungen abgesichert werden kann.
 *
 * - Patient-IDs in Breadcrumb-URLs und in `event.request.url` werden durch
 *   `[REDACTED]` ersetzt.
 * - `event.request.data` und `event.request.cookies` werden komplett
 *   geloescht (koennten Patientendaten enthalten).
 *
 * Der Input wird nicht geklont – wir mutieren `event` in-place, weil der
 * Sentry-SDK das Ergebnis danach weiterreicht und wir keine ueberfluessigen
 * Kopien in Error-Pfaden wollen.
 */
export function stripPiiFromEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  // PII aus Breadcrumbs entfernen
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => {
      if (bc.data?.url && typeof bc.data.url === 'string') {
        bc.data.url = bc.data.url.replace(PATIENT_ID_IN_URL_RE, PATIENT_REDACTED_PATH);
      }
      return bc;
    });
  }

  // Request-Body nie mitsenden (koennte Patientendaten enthalten)
  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
  }

  // Defense in depth: scrub the URL one more time at the top level.
  if (event.request?.url) {
    event.request.url = event.request.url.replace(PATIENT_ID_IN_URL_RE, PATIENT_REDACTED_PATH);
  }

  // PHI scrub for free-text and structured payloads. Covers
  // event.message, event.extra, event.contexts, event.tags, plus the
  // exception message + value chain. Keys on the deny-list lose their
  // entire value; strings get UUID/KVNR/email patterns rewritten.
  if (typeof event.message === 'string') {
    event.message = redactPhiInString(event.message);
  }
  if (event.extra) {
    event.extra = redactPhiInValue(event.extra) as typeof event.extra;
  }
  if (event.contexts) {
    event.contexts = redactPhiInValue(event.contexts) as typeof event.contexts;
  }
  if (event.tags) {
    event.tags = redactPhiInValue(event.tags) as typeof event.tags;
  }
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((ex) => ({
      ...ex,
      value: typeof ex.value === 'string' ? redactPhiInString(ex.value) : ex.value,
    }));
  }
  // Breadcrumb messages can carry stack-text or fetch URLs.
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => ({
      ...bc,
      message: typeof bc.message === 'string' ? redactPhiInString(bc.message) : bc.message,
      data: bc.data ? (redactPhiInValue(bc.data) as typeof bc.data) : bc.data,
    }));
  }

  return event;
}

export function initSentry() {
  if (!env.sentryDsn) return;

  Sentry.init({
    dsn: env.sentryDsn,
    environment: env.mode,
    release: `nutriflow-pro@${__APP_VERSION__}`,

    // Performance: nur 10% der Transaktionen in Produktion. Damit das
    // Sample-Rate-Feld ueberhaupt etwas tut, brauchen wir eine
    // Browser-Tracing-Integration. Ohne sie werden keine Transactions
    // erzeugt und Sentry liefert keine Performance-Daten – das war der
    // Audit-Befund P1-13.
    tracesSampleRate: env.isProd ? 0.1 : 1.0,

    // Welche Origins beim Distributed-Tracing einen `sentry-trace`-Header
    // bekommen sollen. Eigene Origins (relative Requests) sowie unsere
    // Backends Supabase und Mistral. Browser-Extensions werden ueber
    // `denyUrls` weiter unten gefiltert.
    tracePropagationTargets: [
      'localhost',
      /^\//,
      /^https:\/\/[^/]+\.supabase\.co/,
      'api.mistral.ai',
    ],

    integrations: [
      // Generelles Browser-Tracing: pageLoad + navigation Spans, plus
      // Auto-Instrumentierung von fetch/XHR. Wird vom React-Router-v6-
      // Wrapper unten so konfiguriert, dass Navigations-Transactions die
      // korrekten Routen-Patterns als Namen tragen (statt voller URLs).
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    // Keine Patientendaten in Sentry senden (DSGVO) – die eigentliche Logik
    // liegt in stripPiiFromEvent(), damit sie unit-testbar ist.
    beforeSend: (event) => stripPiiFromEvent(event),

    // Sensitive Felder ignorieren
    denyUrls: [
      // Browser-Extensions ausschließen
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });
}

/**
 * Attach the *organisation id* to every Sentry event for the rest of
 * the session. This lets us filter "errors from clinic X" without
 * exposing any individual user.
 *
 * **Never** call this with a patient id, email, name or any other
 * personally identifiable field.
 */
export function setSentryUser(orgId: string | null): void {
  if (!env.sentryDsn) return;
  if (!orgId) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    // Sentry's "user.id" field is the only piece of identity we set.
    // The org id is not personally identifiable: it's a UUID for an
    // organisation, not a person.
    id: orgId,
  });
  Sentry.setTag('organization_id', orgId);
}

/**
 * Add a contextual breadcrumb when a feature flag is toggled by an
 * admin. Useful for debugging "the customer says feature X disappeared"
 * tickets.
 */
export function breadcrumbFeatureToggle(feature: string, enabled: boolean): void {
  if (!env.sentryDsn) return;
  Sentry.addBreadcrumb({
    category: 'feature-flag',
    message: `${enabled ? 'enabled' : 'disabled'} ${feature}`,
    level: 'info',
  });
}

export { Sentry };
